import {
  CompRecommendationOutput,
  MILES_TO_METERS,
  TKV2_ORDER_ATTACHMENT_API,
  Tkv2OrderAttachmentBody,
  schRecommendedCompsFilters,
} from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useGetCompRecommendation } from '../../../hooks/api/schema-hooks/useGetCompRecommendation.ts';
import {
  GetCompRecommendationError,
  GetRentalCompRecommendationError,
  useGetRentalCompRecommendation,
  usePostCompRecommendation,
  usePostRentalCompRecommendation,
} from '../../../hooks/api/toolkitComponents.ts';
import { selectSubjectPropertyDataEntity } from '../../../redux/features/selectors/selectSubjectPropertyDataEntity.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { CompSearch } from '../types.ts';
import { coordinatesFromSearch } from '../utils/coordinatesFromSearch.ts';

type RecommendedPropertiesShared = {
  isIdle: boolean;
  isLoading: boolean;
  isError: boolean;
  getProperties: (props: { search: CompSearch }) => void;
};

type SalesRecommendedPropertiesResult = RecommendedPropertiesShared & {
  data?: CompRecommendationOutput;
};

type RentalRecommendedPropertiesResult = RecommendedPropertiesShared & {
  data?: Tkv2OrderAttachmentBody<'rentalCompRecommendation'>;
};

export const useRecommendedProperties = ({
  orderID,
  compType = 'sales',
}: {
  orderID: string;
  compType?: 'sales' | 'rental';
}): SalesRecommendedPropertiesResult | RentalRecommendedPropertiesResult => {
  const isRental = compType === 'rental';
  const salesMutator = usePostCompRecommendation();
  const rentalMutator = usePostRentalCompRecommendation();
  const attachmentQueryVariables = { pathParams: { orderID } };
  const salesAttachmentQuery = useGetCompRecommendation(attachmentQueryVariables, {
    enabled: !isRental,
    retry: (_failureCount, error: GetCompRecommendationError) => error.status !== 404,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
  const rentalAttachmentQuery = useGetRentalCompRecommendation(attachmentQueryVariables, {
    enabled: isRental,
    retry: (_failureCount, error: GetRentalCompRecommendationError) => error.status !== 404,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
  const queryClient = useQueryClient();
  const source = 'z';
  const locationBoundary = useAppSelector(selectSubjectPropertyDataEntity)?.locationBoundary;

  const getProperties = ({ search }: { search: CompSearch }) => {
    if (isRental) {
      if (rentalMutator.isPending) return;

      const body = TKV2_ORDER_ATTACHMENT_API.RENTAL_COMP_RECOMMENDATION.POST.REQUEST.BODY.parse({
        coordinates: coordinatesFromSearch(search, locationBoundary),
        radius:
          search.geoSearch.t === 'radius'
            ? search.geoSearch.radiusInMeters / MILES_TO_METERS
            : undefined,
        filters: schRecommendedCompsFilters.parse(search.filters),

        matchCriteria: search.matchCriteria,
      });
      rentalMutator.mutate(
        {
          pathParams: { orderID },
          queryParams: { source: 'abode' },
          body,
        } as Parameters<typeof rentalMutator.mutate>[0],
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['api', 'v1', 'account'] });
            rentalAttachmentQuery.refetch();
          },
        },
      );

      return;
    }

    if (salesMutator.isPending) return;

    const body = TKV2_ORDER_ATTACHMENT_API.COMP_RECOMMENDATION.POST.REQUEST.BODY.parse({
      coordinates: coordinatesFromSearch(search, locationBoundary),
      radius:
        search.geoSearch.t === 'radius'
          ? search.geoSearch.radiusInMeters / MILES_TO_METERS
          : undefined,
      filters: schRecommendedCompsFilters.parse(search.filters),
      matchCriteria: search.matchCriteria,
    });
    salesMutator.mutate(
      { pathParams: { orderID }, queryParams: { source }, body },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['api', 'v1', 'account'] });
          salesAttachmentQuery.refetch();
        },
      },
    );
  };

  const salesData =
    !isRental &&
    !salesAttachmentQuery.isLoading &&
    !salesAttachmentQuery.isError &&
    salesAttachmentQuery.data
      ? salesAttachmentQuery.data.body
      : undefined;

  const rentalData =
    !isRental ||
    rentalAttachmentQuery.isLoading ||
    rentalAttachmentQuery.isError ||
    !rentalAttachmentQuery.data
      ? undefined
      : (() => {
          const out = rentalAttachmentQuery.data
            .body as Tkv2OrderAttachmentBody<'rentalCompRecommendation'>;
          return out.body === null ? undefined : out;
        })();

  return {
    isIdle: isRental ? rentalMutator.isIdle : salesMutator.isIdle,
    isLoading: isRental
      ? rentalMutator.isPending || rentalAttachmentQuery.isFetching
      : salesMutator.isPending || salesAttachmentQuery.isFetching,
    isError: isRental ? rentalMutator.isError : salesMutator.isError,
    data: isRental ? rentalData : salesData,
    getProperties,
  } as SalesRecommendedPropertiesResult | RentalRecommendedPropertiesResult;
};
